// Helpers for detection rules whose alerts represent aggregated activity over a
// detection window rather than a single source event. Aggregation events use
// snake_case keys (mailbox_owner_upn, set_ClientIP, ...) and span a window.
// Shared by AlertsPage, TestRunResultsPage, and AggregationSummary.

// Detect aggregation-rule rawEventJson. Returns the parsed object when it
// carries aggregation markers, else null.
export function parseAggregationFields(rawEventJson) {
  if (!rawEventJson || typeof rawEventJson !== 'string' || rawEventJson.startsWith('blob:')) return null;
  let obj;
  try { obj = JSON.parse(rawEventJson); } catch { return null; }
  if (!obj || typeof obj !== 'object') return null;
  const isAggregation =
    obj.detection_window_start || obj.detection_window_end ||
    obj.access_count != null || obj.folder_count != null ||
    obj.mailbox_owner_upn || Array.isArray(obj.set_MailboxOwnerUPN);
  return isAggregation ? obj : null;
}

// The MailItemsAccessed access type of one source event: 'Sync' | 'Bind' | ''.
// Microsoft carries it in OperationProperties[Name=MailAccessType] (an array, or a
// JSON string after some hops); a flattened `MailAccessType` is accepted too.
export function mailAccessTypeOf(se) {
  if (!se || typeof se !== 'object') return '';
  if (se.MailAccessType) return String(se.MailAccessType);
  let props = se.OperationProperties;
  if (typeof props === 'string') { try { props = JSON.parse(props); } catch { props = null; } }
  if (!Array.isArray(props)) return '';
  const p = props.find((x) => x && x.Name === 'MailAccessType');
  return p && p.Value ? String(p.Value) : '';
}

// The folder a Sync event synchronized. A Sync records the FOLDER only -- Outlook's
// cache downloaded it, and Microsoft does not list the messages in it -- so this is
// all such an event says about what was reached. Path is often "Not Available", so
// the name is preferred.
export function syncedFolderOf(se) {
  const pf = se && se.Item && se.Item.ParentFolder;
  if (!pf) return '';
  if (pf.Name) return String(pf.Name);
  if (pf.Path && pf.Path !== 'Not Available') return String(pf.Path);
  return '';
}

// Extract the mailbox owner UPN from an aggregation event, when present. Used
// as a fallback "who" identifier on rules where alert.user_id holds an internal
// LogonUserSid (delegate access) instead of a person.
export function aggregationOwnerUpn(rawEventJson) {
  const agg = parseAggregationFields(rawEventJson);
  if (!agg) return null;
  if (agg.mailbox_owner_upn) return agg.mailbox_owner_upn;
  if (Array.isArray(agg.set_MailboxOwnerUPN) && agg.set_MailboxOwnerUPN.length === 1) {
    return agg.set_MailboxOwnerUPN[0];
  }
  return null;
}
